import { SubAccount } from 'vopay';
import { Row } from './Table/Table';
import { FlatSubAccountWithTree, NestedSubAccount, SubAccountsListsShow } from './types';

// Pure row-mapping helpers, ported verbatim from client-dashboard's SubAccountsPage (ID-13193). Split out of
// the root because both the list root (allAccountsBlocked) and the table part (rows) need them, and the host
// still feeds its own org-chart fetch through buildNested.

export function mapBalances(subAccounts: SubAccount[]): SubAccountsListsShow[] {
  const subAccountsShow: SubAccountsListsShow[] = [];

  subAccounts.forEach((subAccount) => {
    const balances = Object.values(subAccount.Balances || {});

    balances.forEach((balance) => {
      // Keep the API's list of children (one per sub-account, not one per currency) for the Sub-Accounts count.
      subAccountsShow.push({ ...subAccount, ...balance, childSubAccounts: subAccount.SubAccounts });
    });
  });

  return subAccountsShow;
}

export function filterActiveAccounts(accounts: NestedSubAccount[]): NestedSubAccount[] {
  return accounts
    .filter((account) => account.IsActive)
    .map((account) => ({
      ...account,
      SubAccounts: account.SubAccounts ? filterActiveAccounts(account.SubAccounts) : undefined,
    }));
}

export function buildFlatWithTree(subAccounts: SubAccountsListsShow[]): FlatSubAccountWithTree[] {
  return subAccounts.map((account) => {
    const parents = account.ParentAccounts;
    if (parents.length === 0) return { ...account, tree: [] };

    const root = parents.find((p) => p.ParentAccountID === null);
    if (!root) return { ...account, tree: parents.map((p) => p.AccountName) };

    const chain: string[] = [root.AccountName];
    let current = parents.find((p) => p.ParentAccountID === root.AccountID);

    while (current) {
      chain.push(current.AccountName);
      const next = parents.find((p) => p.ParentAccountID === current!.AccountID);
      current = next;
    }

    // slice(1) excludes the root (the logged-in account) to match the nested view tree convention
    return { ...account, tree: chain.slice(1) };
  });
}

export function buildNested(subAccounts: SubAccount[]): { nested: NestedSubAccount[] } {
  function toNestedRows(account: SubAccount): NestedSubAccount[] {
    const childRows = (account.SubAccounts || []).flatMap(toNestedRows);

    return mapBalances([account]).map((balanceRow) => ({
      ...balanceRow,
      SubAccounts: childRows,
      Key: `${balanceRow.AccountID}-${balanceRow.Currency}`,
    }));
  }

  return { nested: subAccounts.flatMap(toNestedRows) };
}

export function mapNestedSubAccounts(nestedSubAccounts: NestedSubAccount[]): Row<NestedSubAccount>[] {
  return (
    nestedSubAccounts.map((account) => ({
      id: `${account.AccountID}-${account.Currency}`,
      data: account,
      subRows: (account.SubAccounts || []).length > 0 ? mapNestedSubAccounts(account.SubAccounts || []) : undefined,
    })) || []
  );
}

export function mapFlatSubAccountWithTree(
  flatSubAccountWithTree: FlatSubAccountWithTree[],
): Row<FlatSubAccountWithTree>[] {
  return (
    flatSubAccountWithTree.map((account) => ({
      id: `${account.AccountID}-${account.Currency}`,
      data: account,
    })) || []
  );
}
